import {useCallback, useState} from "react";
import * as XLSX from "xlsx";
import {FilterQueryParams} from "@/components/paginated-selector/filters/types";
import {PaginatedResponseV2} from "@/types/PaginatedResponse";

type ExportColumn<T> = {
    header: string;
    getValue: (item: T) => string | number | boolean | null | undefined;
    width?: number;
};

type UseExportToXlsxParams<T> = {
    getItems: (search: string, page: number, filterParams?: FilterQueryParams) => Promise<PaginatedResponseV2<T> | null>;
    columns: ExportColumn<T>[];
    filename: string;
    exportPageSize?: number; // Number of items to fetch per page during export (default: 100)
};

export function useExportToXlsx<T>({getItems, columns, filename, exportPageSize = 100}: UseExportToXlsxParams<T>) {
    const [isExporting, setIsExporting] = useState(false);
    const [progress, setProgress] = useState<{current: number; total: number} | null>(null);

    const exportToXlsx = useCallback(async (search: string, filterParams: FilterQueryParams) => {
        setIsExporting(true);
        setProgress(null);

        try {
            // Create export-specific params with larger page size
            const exportParams = {...filterParams};

            // Get first page to determine total count (use page size 1 just to get the count)
            const firstPage = await getItems(search, 1, {...exportParams, limit: 1});
            if (!firstPage) {
                throw new Error("Failed to fetch data");
            }

            const totalCount = firstPage.totalResults ?? 0;
            const totalPages = Math.ceil(totalCount / exportPageSize);
            let allItems: T[] = [];

            // Fetch all pages with the export page size
            for (let page = 1; page <= totalPages; page++) {
                setProgress({current: page, total: totalPages});
                const pageData = await getItems(search, page, {...exportParams, limit: exportPageSize});
                if (pageData) {
                    allItems = [...allItems, ...pageData.results];
                }
            }

            // Build worksheet data
            const worksheetData = [
                // Header row
                columns.map(col => col.header),
                // Data rows
                ...allItems.map(item =>
                    columns.map(col => {
                        const value = col.getValue(item);
                        // Handle null/undefined
                        if (value === null || value === undefined) return "";
                        return value;
                    })
                )
            ];

            // Create workbook and worksheet
            const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
            if (columns.some(col => col.width != null)) {
                worksheet["!cols"] = columns.map(col => col.width ? {wch: col.width} : {});
            }
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Data");

            // Generate filename with timestamp
            const timestamp = new Date().toISOString().replace(/:/g, "-").split(".")[0];
            const fullFilename = `${filename}_${timestamp}.xlsx`;

            // Download file
            XLSX.writeFile(workbook, fullFilename);

            setProgress(null);
        } catch (error) {
            console.error("Export failed:", error);
            throw error;
        } finally {
            setIsExporting(false);
        }
    }, [getItems, columns, filename, exportPageSize]);

    return {
        exportToXlsx,
        isExporting,
        progress
    };
}
